import React, { useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ToastSetupGuide from "../components/ToastSetupGuide";
import LinkIcon from "@mui/icons-material/Link";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import {
  API_get_toast_credential_link,
  API_submit_toast_credential_link,
} from "../constants";

const STATE_LOADING = "loading";
const STATE_ACTIVE = "active";
const STATE_SUBMITTED = "submitted";
const STATE_EXPIRED = "expired";
const STATE_INVALIDATED = "invalidated";
const STATE_NOT_FOUND = "not_found";
const STATE_SUCCESS = "success";
const STATE_ERROR = "error";

// Reuse the violet/slate palette from Rightwork's integration settings.
const ACCENT = "#6d28d9";
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px !important",
    minHeight: "55px",
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
  },
  "& .MuiInputBase-input": { fontFamily: "monospace", overflowWrap: "anywhere" },
};
const focusSx = {
  "&:focus-visible, &.Mui-focusVisible": {
    outline: `2px solid ${ACCENT}`,
    outlineOffset: "3px",
  },
};

function ToastCredentialConnect({ token }) {
  const [pageState, setPageState] = useState(STATE_LOADING);
  const [link, setLink] = useState(null);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [loadAttempt, setLoadAttempt] = useState(0);

  useEffect(() => {
    setPageState(STATE_LOADING);
    setLink(null);
    setErrorMessage("");
    setClientId("");
    setClientSecret("");
    let cancelled = false;

    async function load() {
      try {
        const res = await axios.get(API_get_toast_credential_link + token);
        if (cancelled) return;
        setLink(res.data);
        setPageState(STATE_ACTIVE);
      } catch (err) {
        if (cancelled) return;
        const data = err?.response?.data;
        if (err?.response?.status === 410) {
          setLink(data || null);
          const status = data?.status;
          if (status === STATE_SUBMITTED) {
            setPageState(STATE_SUBMITTED);
          } else if (status === STATE_INVALIDATED) {
            setPageState(STATE_INVALIDATED);
          } else {
            setPageState(STATE_EXPIRED);
          }
        } else if (err?.response?.status === 404) {
          setPageState(STATE_NOT_FOUND);
        } else {
          setPageState(STATE_ERROR);
        }
      }
    }

    if (token) {
      load();
    } else {
      setPageState(STATE_NOT_FOUND);
    }

    return () => {
      cancelled = true;
    };
  }, [token, loadAttempt]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setErrorMessage("");

    if (!clientId.trim() || !clientSecret.trim()) {
      setErrorMessage("Client ID and Client Secret are required.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(API_submit_toast_credential_link, {
        token,
        client_id: clientId.trim(),
        client_secret: clientSecret.trim(),
      });
      setClientId("");
      setClientSecret("");
      setLink(res.data);
      setPageState(STATE_SUCCESS);
    } catch (err) {
      const data = err?.response?.data;
      if (err?.response?.status === 410) {
        const status = data?.status;
        if (status === STATE_SUBMITTED) {
          setPageState(STATE_SUBMITTED);
        } else if (status === STATE_INVALIDATED) {
          setPageState(STATE_INVALIDATED);
        } else {
          setPageState(STATE_EXPIRED);
        }
      } else if (err?.response?.status === 429) {
        setErrorMessage(
          data?.detail || "Too many attempts. Please try again in a few minutes."
        );
      } else if (data?.detail) {
        setErrorMessage(data.detail);
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    }
  }

  function renderStatus(title, body, options = {}) {
    return (
      <Box role="status">
        {options.success && (
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#dcfce7",
              color: "#16a34a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 38 }} />
          </Box>
        )}
        <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography color="textSecondary">{body}</Typography>
        {options.success && (
          <Box sx={{ mt: 3, p: 2.5, backgroundColor: "#f8fafc", borderRadius: "12px", border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>What happens next?</Typography>
            <Typography variant="body2" color="textSecondary">
              Toast data syncs separately and may not appear in Rightwork immediately.
              Your Rightwork contact can help confirm that your locations and data are ready.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 500 }}>
              You can safely close this page. There’s no need to submit these credentials again.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f8fafc",
        padding: { xs: "24px 16px", sm: "40px 24px" },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 600,
          padding: { xs: "28px 20px", sm: "36px 40px" },
          borderRadius: "16px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 12px 40px rgba(28, 37, 65, 0.06)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2.5 }, mb: 3.5 }}>
          <Box component="img" src="/images/full_logo.png" alt="Rightwork" sx={{ width: { xs: 104, sm: 152 }, height: "auto" }} />
          <Box sx={{ display: "flex", p: 1, borderRadius: "50%", color: ACCENT, backgroundColor: "#f5f3ff" }}>
            <LinkIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box component="img" src="/images/toast-logo.svg" alt="Toast" sx={{ width: { xs: 80, sm: 104 }, height: "auto" }} />
        </Box>
        {pageState === STATE_LOADING && (
          <Typography align="center" color="textSecondary">
            Loading...
          </Typography>
        )}

        {pageState === STATE_ACTIVE && (
          <>
            <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Connect Toast to Rightwork
            </Typography>
            <Typography color="textSecondary" sx={{ mb: 3 }}>
              Securely connect Toast for <Box component="strong" sx={{ color: "#334155", overflowWrap: "anywhere" }}>{link?.company_name || "your company"}</Box>.
            </Typography>

            <ToastSetupGuide />

            <form data-sentry-block aria-label="Toast credentials" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="toast-client-id" className="text-slate-600 text-sm font-medium">Client ID</label>
                <div className="mt-1.5">
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="toast-client-id"
                    name="client_id"
                    disabled={submitting}
                    placeholder="Paste your Client ID"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    autoComplete="off"
                    sx={fieldSx}
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="toast-client-secret" className="text-slate-600 text-sm font-medium">Client Secret</label>
                <div className="mt-1.5">
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    id="toast-client-secret"
                    name="client_secret"
                    disabled={submitting}
                    placeholder="Paste your Client Secret"
                    type={showSecret ? "text" : "password"}
                    value={clientSecret}
                    onChange={(e) => setClientSecret(e.target.value)}
                    autoComplete="off"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={showSecret ? "Hide Client Secret" : "Show Client Secret"}
                            aria-pressed={showSecret}
                            disabled={submitting}
                            sx={focusSx}
                            onClick={() => setShowSecret(!showSecret)}
                            edge="end"
                          >
                            {showSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                </div>
              </div>

              {errorMessage && (
                <Typography role="alert" color="error" variant="body2" sx={{ mt: 2 }}>
                  {errorMessage}
                </Typography>
              )}

              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 3 }}>
                <LockOutlinedIcon sx={{ color: "#64748b", fontSize: 18, mt: 0.25 }} />
                <Typography color="textSecondary" variant="body2">
                  Your credentials are sent over HTTPS and stored in Rightwork's
                  encrypted secret store. They are not emailed to Rightwork.
                </Typography>
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={submitting || !clientId.trim() || !clientSecret.trim()}
                sx={{
                  mt: 3,
                  py: 1.4,
                  borderRadius: "8px",
                  backgroundColor: ACCENT,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: "#5b21b6", boxShadow: "none" },
                  "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#64748b" },
                  ...focusSx,
                }}
              >
                {submitting ? "Connecting..." : "Connect Toast"}
              </Button>
            </form>
          </>
        )}

        {pageState === STATE_SUCCESS &&
          renderStatus(
            "Toast connected",
            <>The credentials for <strong>{link?.company_name || "your company"}</strong> were validated and securely saved.</>,
            { success: true }
          )}

        {pageState === STATE_SUBMITTED &&
          renderStatus(
            "Credentials already received",
            "This secure link has already been used. You don’t need to submit your credentials again. If you need to update them, contact your company admin to update the credentials in Rightwork."
          )}

        {pageState === STATE_EXPIRED &&
          renderStatus(
            "Link expired",
            "This secure Toast credential link is no longer active. Ask the sender or your company admin for a new link."
          )}

        {pageState === STATE_INVALIDATED &&
          renderStatus(
            "Link no longer active",
            "A newer link or a completed POS connection may have replaced this one. Ask the sender or your company admin for help."
          )}

        {pageState === STATE_NOT_FOUND &&
          renderStatus(
            "Link not found",
            "This secure Toast credential link could not be found. Check the URL or ask the sender or your company admin for a new link."
          )}

        {pageState === STATE_ERROR && <>
          {renderStatus(
            "Unable to load link",
            "Something went wrong loading this secure Toast credential link."
          )}
          <Button variant="contained" sx={{ mt: 2, ...focusSx }} onClick={() => setLoadAttempt(attempt => attempt + 1)}>Retry loading link</Button>
        </>}
      </Paper>
    </Box>
  );
}

export default ToastCredentialConnect;
